import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { CameraIcon, PhotoIcon, VideoCameraIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import Modal from "../../../layouts/Modal";
import LoadingWheel from "../../shared/LoadingWheel";
import SelectInput from "../../shared/Inputs/SelectInput";
import { TextInput } from "../../shared/Inputs/TextInput";
import useNotification from "../../notifications/useNotifications";
import FileUpload from "../../shared/Inputs/FileUpload";

import {
  ICheckInItem,
  ICheckInStep,
} from "../../../models/CheckIn";
import { DocumentType, IDocument } from "../../../models/Document";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useLeads from "../../../data/useLeads";

export default function LeadCheckInModal({
  open,
  setOpen,
  vehicleId,
  leadId,
  showTryCompleteLead = true,
  steps,
  items,
  documents,
  isLoading,
  saveCheckInItem,
  uploadCheckInItemDocuments,
  downloadCheckInItemDocument,
  deleteCheckInItemDocument,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  vehicleId: string;
  leadId?: string;
  showTryCompleteLead?: boolean;
  steps: ICheckInStep[];
  items: ICheckInItem[];
  documents: IDocument[];
  isLoading: boolean;
  saveCheckInItem: {
    mutate: (body: any, opts?: any) => void;
  };
  uploadCheckInItemDocuments: {
    mutate: (body: any, opts?: any) => void;
    isLoading?: boolean;
  };
  downloadCheckInItemDocument: {
    mutate: (body: any, opts?: any) => void;
  };
  deleteCheckInItemDocument: {
    mutate: (body: any, opts?: any) => void;
  };
}) {
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { bulkComplete } = useLeads(true, "LeadCheckInModal");

  const [savingStepId, setSavingStepId] = useState<string | null>(null);
  const [uploadingStepId, setUploadingStepId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [filesByStepId, setFilesByStepId] = useState<Record<string, File[]>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState("");
  const [isVideo, setIsVideo] = useState(false);
  const [mediaUploadOpen, setMediaUploadOpen] = useState(false);
  const [mediaUploadStep, setMediaUploadStep] = useState<ICheckInStep | null>(null);
  const [mediaUploadItem, setMediaUploadItem] = useState<ICheckInItem | undefined>(undefined);

  const checkInRows = useMemo(() => {
    return steps.map((step) => {
      const item = items.find((x) => x.checkInStepId === step.id);
      return {
        step,
        item,
        isCompleted: !!item?.completed,
      };
    });
  }, [steps, items]);

  const { register, setValue: rhfSetValue, watch, reset } = useForm<Record<string, string>>();

  const isCompletedByStepId = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const row of checkInRows) {
      map[row.step.id] = row.isCompleted;
    }
    return map;
  }, [checkInRows]);

  const documentsByCheckInItemId = useMemo(() => {
    const map: Record<string, IDocument[]> = {};
    for (const doc of documents) {
      if (!doc.checkInItemId) continue;
      map[doc.checkInItemId] = [...(map[doc.checkInItemId] ?? []), doc];
    }
    return map;
  }, [documents]);

  const parseOptions = (options: string | null) => {
    if (!options) return [];
    return options
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  };

  useEffect(() => {
    const nextValues: Record<string, string> = {};
    for (const item of items) {
      nextValues[item.checkInStepId] = item.value ?? "";
    }
    reset(nextValues);
  }, [items, reset]);

  const saveValueKeepingCompletion = (stepId: string, value: string) => {
    if (!vehicleId) return;

    setSavingStepId(stepId);
    saveCheckInItem.mutate(
      {
        vehicleId,
        checkInStepId: stepId,
        value,
        isCompleted: isCompletedByStepId[stepId] ?? false,
      },
      {
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to update check-in item",
          });
        },
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const saveCheckInItemAsync = (stepId: string, value: string, isCompleted: boolean) => {
    return new Promise<ICheckInItem>((resolve, reject) => {
      saveCheckInItem.mutate(
        {
          vehicleId,
          checkInStepId: stepId,
          value,
          isCompleted,
        },
        {
          onSuccess: (res: any) => resolve(res.data),
          onError: reject,
        }
      );
    });
  };

  const toggleCompletion = (stepId: string, nextCompleted: boolean) => {
    if (!vehicleId) return;

    const currentValue = watch(stepId) ?? "";

    setSavingStepId(stepId);
    saveCheckInItem.mutate(
      {
        vehicleId,
        checkInStepId: stepId,
        value: currentValue,
        isCompleted: nextCompleted,
      },
      {
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to update check-in completion",
          });
        },
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const registerWithSaveOnBlur = (stepId: string) =>
    register(stepId, {
      onBlur: (e) => {
        const target = e.target as HTMLInputElement;
        saveValueKeepingCompletion(stepId, target.value ?? "");
      },
    });

  const setValueSaving = (stepId: string, nextValue: any) => {
    rhfSetValue(stepId, nextValue);
    saveValueKeepingCompletion(stepId, String(nextValue ?? ""));
  };

  const updateFilesForStep = (stepId: string, files: File[]) => {
    setFilesByStepId((prev) => ({
      ...prev,
      [stepId]: files,
    }));
  };

  const handleOpenMediaUpload = (step: ICheckInStep, item?: ICheckInItem) => {
    setMediaUploadStep(step);
    setMediaUploadItem(item);
    setMediaUploadOpen(true);
  };

  const handleUploadDocuments = async (step: ICheckInStep, item?: ICheckInItem) => {
    const files = filesByStepId[step.id] ?? [];
    if (files.length === 0) return;

    try {
      setUploadingStepId(step.id);

      const checkInItem = item ?? await saveCheckInItemAsync(step.id, watch(step.id) ?? "", isCompletedByStepId[step.id] ?? false);
      const formData = new FormData();

      for (const file of files) {
        formData.append(DocumentType[DocumentType.Vehicle].toString(), file);
      }

      uploadCheckInItemDocuments.mutate(
        {
          checkInItemId: checkInItem.id,
          formData,
        },
        {
          onSuccess: () => {
            setFilesByStepId((prev) => ({
              ...prev,
              [step.id]: [],
            }));
            setMediaUploadOpen(false);
            addNotification({
              variant: "success",
              primaryText: "Check-in media uploaded",
            });
          },
          onError: (error: any) => {
            addNotification({
              variant: "error",
              primaryText: error?.message ?? "Unable to upload check-in media",
            });
          },
          onSettled: () => setUploadingStepId(null),
        }
      );
    } catch {
      setUploadingStepId(null);
      addNotification({
        variant: "error",
        primaryText: "Unable to create check-in item for media",
      });
    }
  };

  const handlePreview = (doc: IDocument) => {
    if (!doc.checkInItemId) return;

    downloadCheckInItemDocument.mutate(
      {
        checkInItemId: doc.checkInItemId,
        documentId: doc.id,
      },
      {
        onSuccess: (file: any) => {
          const url = window.URL.createObjectURL(file);

          setIsVideo(file.type.includes("video"));
          setPreview(url);
          setPreviewOpen(true);
        }
      }
    );
  };

  const handleDeleteDocument = (doc: IDocument) => {
    if (!doc.checkInItemId) return;

    deleteCheckInItemDocument.mutate(
      {
        checkInItemId: doc.checkInItemId,
        documentId: doc.id,
      },
      {
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to delete check-in media",
          });
        }
      }
    );
  };

  const handleTryToComplete = () => {
    if (!leadId) return;

    const body = {
      leads: [leadId]
    };

    bulkComplete.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setOpen(false);
        setConfirmOpen(false);
      }
    });
  }

  return (
    <>
      {showTryCompleteLead && !!leadId && (
        <ConfirmModal open={confirmOpen} setOpen={setConfirmOpen}
          title={"Try To Complete?"}
          message={"This will raise a payment, attempt to COD the vehicle and generate an invoice."}
          confirmButtonText={"Confirm"}
          onConfirm={handleTryToComplete}
          isLoading={bulkComplete.isLoading}
        />
      )}

      <Modal open={previewOpen} setOpen={setPreviewOpen} width="max-w-5xl">
        {isVideo ? (
          <video controls className="w-full max-h-[75vh]" src={preview} />
        ) : (
          <img src={preview} alt="Check-in media preview" className="w-full max-h-[75vh] object-contain" />
        )}
      </Modal>

      <Modal open={mediaUploadOpen} setOpen={setMediaUploadOpen} width="max-w-4xl">
        {mediaUploadStep && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Upload Media</h3>
              <p className="text-sm text-gray-600">{mediaUploadStep.name}</p>
            </div>

            <FileUpload
              key={mediaUploadStep.id}
              accept=".jpg,.jpeg,.png,.mp4"
              label="Upload Media"
              desc="Images or video"
              multiple
              updateFilesCb={(files: File[]) => updateFilesForStep(mediaUploadStep.id, files)}
              maxFileSizeInBytes={52428800}
            />

            <div className="flex justify-end gap-3">
              <ButtonInput
                label="Cancel"
                onClick={() => setMediaUploadOpen(false)}
                classes="!bg-white border border-gray-200 !text-primary-500 hover:!bg-gray-50"
              />
              <ButtonInput
                label="Upload"
                onClick={() => handleUploadDocuments(mediaUploadStep, mediaUploadItem)}
                classes={undefined}
                disabled={(filesByStepId[mediaUploadStep.id] ?? []).length === 0}
                isLoading={uploadingStepId === mediaUploadStep.id}
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} setOpen={setOpen as any} width="max-w-6xl">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Check-In Tasks</h3>
              <p className="text-sm text-gray-600">Complete the remaining check-in items for this vehicle.</p>
            </div>
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>

          {isLoading ? (
            <div className="py-6">
              <LoadingWheel />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Value</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Media</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {checkInRows.map(({ step, item, isCompleted }) => {
                  const itemDocuments = item ? documentsByCheckInItemId[item.id] ?? [] : [];
                  return (
                  <tr key={step.id} className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm align-top">{step.name}</td>
                    <td className="px-4 py-2 text-sm">
                      {step.needsValue ? (
                        (() => {
                          const parsedOptions = parseOptions(step.options);
                          if (parsedOptions.length > 0) {
                            if (parsedOptions.length <= 3) {
                              const selectedValue = watch(step.id) ?? item?.value ?? "";

                              return (
                                <div className="flex flex-wrap gap-2">
                                  {parsedOptions.map((opt) => {
                                    const selected = selectedValue === opt;

                                    return (
                                      <button
                                        key={opt}
                                        type="button"
                                        disabled={savingStepId === step.id}
                                        onClick={() => setValueSaving(step.id, opt)}
                                        className={`rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm ${
                                          selected
                                            ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700"
                                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                        }`}
                                      >
                                        {opt}
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            }

                            return (
                              <div className="w-full">
                                <SelectInput
                                  label=""
                                  register={register}
                                  registerName={step.id}
                                  options={parsedOptions.map((opt) => ({ id: opt, name: opt }))}
                                  setValue={setValueSaving}
                                  watch={watch}
                                  disabled={savingStepId === step.id}
                                  cols="w-full"
                                  size="sm"
                                />
                              </div>
                            );
                          }

                          return (
                            <TextInput
                              label=""
                              register={registerWithSaveOnBlur as any}
                              registerName={step.id}
                              initialValue={item?.value ?? ""}
                              disabled={savingStepId === step.id}
                              placeholder="Enter value"
                              cols="w-full"
                              size="sm"
                            />
                          );
                        })()
                      ) : (
                        <span className="text-gray-400">{item?.value || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm align-top">
                      {step.needsImage ? (
                        <div className="space-y-2">
                          {itemDocuments.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {itemDocuments.map((doc) => (
                                <div key={doc.id} className="relative h-12 w-12 rounded border border-gray-200 p-1">
                                  <XCircleIcon className="absolute -top-2 -right-2 h-5 w-5 cursor-pointer rounded-full bg-white text-red-600" onClick={() => handleDeleteDocument(doc)} />
                                  <button type="button" className="flex h-full w-full items-center justify-center" onClick={() => handlePreview(doc)}>
                                    {doc.thumbnail ? (
                                      <img src={`data:image/png;base64, ${doc.thumbnail}`} alt={doc.fileName ?? "Check-in media"} className="h-full w-full object-contain" />
                                    ) : doc.contentType?.includes("video") ? (
                                      <VideoCameraIcon className="h-6 w-6 text-gray-500" />
                                    ) : (
                                      <PhotoIcon className="h-6 w-6 text-gray-500" />
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-primary-600"
                            onClick={() => handleOpenMediaUpload(step, item)}
                            disabled={uploadingStepId === step.id}
                          >
                            <CameraIcon className="h-4 w-4" />
                            <span className="sr-only">Upload media</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center"
                        disabled={savingStepId === step.id}
                        onClick={() => toggleCompletion(step.id, !isCompleted)}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <span className="h-5 w-5 rounded-full border border-gray-300 inline-block" />
                        )}
                      </button>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          )}

          <div className="flex justify-between">
            {showTryCompleteLead && !!leadId && (
              <ButtonInput
                label="Try to Complete Lead"
                onClick={() => setConfirmOpen(true)}
                classes={undefined}
              />
            )}

            <ButtonInput
              label="Check-In Done"
              onClick={() => setOpen(false)}
              classes={undefined}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

